import React, { useMemo } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SkuDailyMarketPrice } from "@/features/sku-dashboard/hooks/use-get-sku";
import { Box, Text } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface Props {
  data: SkuDailyMarketPrice[];
  // 포이즌 일별 체결가(poizon_trade_daily) — 크림 체결가와 함께 겹쳐 그린다.
  // 선별 수집된 상품에만 있어서 비어 있는 sku가 많다.
  poizonData?: SkuDailyMarketPrice[];
  supplyPriceKrw?: number;
  supplyPriceLabel?: string;
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function formatPrice(value: number) {
  // 시장 판매가는 정수로 표시 (소수점 노출 방지)
  return Math.round(value).toLocaleString();
}

export default function SkuPriceChart({
  data,
  poizonData = [],
  supplyPriceKrw,
  supplyPriceLabel,
}: Props) {
  const { t, tTitle } = useI18nHelper();

  // 크림·포이즌은 관측 날짜가 서로 달라 날짜 합집합으로 병합한다.
  // 값이 없는 날은 키 자체를 비워 두고 connectNulls로 선을 잇는다.
  const chartData = useMemo(() => {
    const byDate = new Map<string, { kream?: number; poizon?: number }>();
    for (const d of data) {
      byDate.set(d.date, { ...byDate.get(d.date), kream: d.price });
    }
    for (const d of poizonData) {
      byDate.set(d.date, { ...byDate.get(d.date), poizon: d.price });
    }
    return [...byDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, prices]) => ({ date: formatDate(date), ...prices }));
  }, [data, poizonData]);

  if (chartData.length === 0) {
    return (
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
        height={"100%"}
      >
        <Text textStyle={"body-md-regular"} color={"#8F8F8C"}>
          {tTitle("no_data")}
        </Text>
      </Box>
    );
  }

  const hasBothSeries = data.length > 0 && poizonData.length > 0;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={chartData}
        margin={{ top: 24, right: 16, left: 0, bottom: 0 }}
      >
        <defs>
          <linearGradient id="skuPriceGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#48B5FE" stopOpacity={0.2} />
            <stop offset="95%" stopColor="#48B5FE" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8E8E5" />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11, fill: "#8F8F8C" }}
          tickMargin={8}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11, fill: "#8F8F8C" }}
          tickFormatter={formatPrice}
          width={72}
        />
        <Tooltip
          formatter={(value: number, name: string) => [formatPrice(value), name]}
          labelStyle={{ color: "#444444", fontWeight: 600 }}
          contentStyle={{
            border: "0.5px solid #D4D4D1",
            borderRadius: "8px",
            fontSize: "12px",
          }}
        />
        {hasBothSeries && (
          <Legend
            verticalAlign={"top"}
            height={24}
            iconSize={10}
            wrapperStyle={{ fontSize: "11px" }}
          />
        )}
        <Area
          type="monotone"
          dataKey="kream"
          name={t("kream_market_price")}
          stroke="#48B5FE"
          strokeWidth={2}
          fill="url(#skuPriceGradient)"
          dot={{ r: 3, fill: "#48B5FE", strokeWidth: 0 }}
          activeDot={{ r: 5 }}
          connectNulls
        />
        {poizonData.length > 0 && (
          <Line
            type="monotone"
            dataKey="poizon"
            name={t("poizon_market_price")}
            stroke="#2D9A5F"
            strokeWidth={2}
            dot={{ r: 3, fill: "#2D9A5F", strokeWidth: 0 }}
            activeDot={{ r: 5 }}
            connectNulls
          />
        )}
        {supplyPriceKrw !== undefined && (
          <ReferenceLine
            y={supplyPriceKrw}
            stroke="#FF6B35"
            strokeWidth={1.5}
            strokeDasharray="5 4"
            label={{
              value: `${supplyPriceLabel ?? tTitle("supply_price")}  ₩${supplyPriceKrw.toLocaleString()}`,
              position: "insideTopLeft",
              fontSize: 11,
              fill: "#FF6B35",
              dy: -8,
            }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
